import {cn} from '@sollapay/ui';
import {ContentSection, Skeleton, Typography} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

export interface InstructionsTabContentSkeletonProps {
  showsAssetColumn?: boolean;
}

// Matches InstructionsTable's column set: buyer, status, asset, amount, method,
// reference, created, actions — kept in sync so the loading state doesn't reflow.
export function InstructionsTabContentSkeleton({
  showsAssetColumn = true
}: InstructionsTabContentSkeletonProps) {
  const {t} = useTranslation('trustAccounts');
  const columnsPrefix = 'view.instructions.columns';

  const gridCols = showsAssetColumn
    ? 'grid-cols-[repeat(7,1fr)_52px]'
    : 'grid-cols-[repeat(6,1fr)_52px]';

  const headers = [
    t(`${columnsPrefix}.buyer`),
    t(`${columnsPrefix}.status`),
    ...(showsAssetColumn ? [t(`${columnsPrefix}.asset`)] : []),
    t(`${columnsPrefix}.amount`),
    t(`${columnsPrefix}.paymentMethod`),
    t(`${columnsPrefix}.reference`),
    t(`${columnsPrefix}.createdAt`)
  ];

  return (
    <ContentSection
      title={t('view.instructions.title')}
      subtitle={t('view.instructions.subtitle')}
      action={<Skeleton className="h-9 w-32 rounded-md" />}
    >
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-3 gap-6">
          {Array.from({length: 3}).map((_, i) => (
            <Skeleton key={i} className="h-[104px] rounded-2xl" />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-9 w-64 rounded-md" />
          <Skeleton className="h-9 w-24 rounded-md" />
        </div>

        <div className="overflow-hidden rounded-xl border border-border-subtle shadow-xs">
          <div
            className={cn(
              'grid gap-4 border-b border-border-subtle bg-background-surface px-4 py-3',
              gridCols
            )}
          >
            {headers.map(label => (
              <Typography key={label} size="xs" color="tertiary">
                {label}
              </Typography>
            ))}
            <div />
          </div>
          {Array.from({length: 4}).map((_, i) => (
            <div
              key={i}
              className={cn(
                'grid items-center gap-4 border-b border-border-subtle px-4 py-3 last:border-b-0',
                gridCols
              )}
            >
              <div className="flex items-center gap-2">
                <Skeleton className="size-8 shrink-0 rounded-full" />
                <Skeleton className="h-4 w-20" />
              </div>
              <Skeleton className="h-5 w-20 rounded-full" />
              {showsAssetColumn && <Skeleton className="h-4 w-24" />}
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-5 w-24 rounded-full" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="size-8 rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </ContentSection>
  );
}
